// The write guard (§8, §15.1). Pure logic; `fs.ts` calls `assertWritable()` before EVERY write.
//
// A write is allowed only when its target (a path relative to the opened folder, '/'-separated) is:
//   · `<source>.epb.json`, `<source>.epb.json.tmp` or `<source>.epb.mask.png` for an opened source; or
//   · inside `exports/` (any depth below it), and not a source name.
//   · `designs/<name>.epd` (or its `.tmp`): a Design workspace document (DRAW_PLAN §1), one level deep.
// Everything else throws — in particular any write whose name equals an opened source file
// (case-insensitive, because Windows and macOS file systems are), and any path with `..`.
import { MASK_SUFFIX, SIDECAR_SUFFIX } from '../model/sidecar';

export class WriteGuardError extends Error {}

export const EXPORTS_DIR = 'exports';
/** Design workspace documents (DRAW_PLAN §1): <folder>/designs/<name>.epd, autosaved (temp + move). */
export const DESIGNS_DIR = 'designs';
export const EPD_EXT = '.epd';
const TMP = '.tmp';

export class WriteGuard {
  private sources = new Set<string>();

  setSources(names: Iterable<string>) {
    this.sources = new Set([...names].map((n) => n.toLowerCase()));
  }
  get sourceCount() {
    return this.sources.size;
  }
  /** True when `name` (any case) is an opened original. */
  isSource(name: string) {
    return this.sources.has(name.toLowerCase());
  }
  /** The opened originals' names (lower case), e.g. to keep export names off them. */
  sourceNames(): string[] {
    return [...this.sources];
  }

  /** Throws unless `relDir` is a valid directory path inside exports/ (no `..`, no empty parts). */
  assertExportDir(relDir: string): string {
    const parts = relDir.replace(/\\/g, '/').split('/');
    if (parts.some((p) => p === '' || p === '.' || p === '..'))
      throw new WriteGuardError(`refused: invalid path "${relDir}"`);
    if (parts[0]!.toLowerCase() !== EXPORTS_DIR)
      throw new WriteGuardError(`refused: "${relDir}" is outside ${EXPORTS_DIR}/`);
    return parts.join('/');
  }

  /** Throws WriteGuardError unless `relPath` may be written. Returns the normalized path. */
  assertWritable(relPath: string): string {
    const parts = relPath.replace(/\\/g, '/').split('/');
    if (parts.some((p) => p === '' || p === '.' || p === '..'))
      throw new WriteGuardError(`refused: invalid path "${relPath}"`);
    const name = parts[parts.length - 1]!;
    const lower = name.toLowerCase();
    if (this.sources.has(lower)) throw new WriteGuardError(`refused: "${name}" is an original photo`);
    if (parts.length === 1) {
      for (const suffix of [SIDECAR_SUFFIX, SIDECAR_SUFFIX + TMP, MASK_SUFFIX]) {
        if (lower.endsWith(suffix) && this.sources.has(lower.slice(0, -suffix.length)))
          return parts.join('/');
      }
      throw new WriteGuardError(`refused: "${name}" is not a sidecar of an opened photo`);
    }
    // designs/<name>.epd (and its .tmp): our own documents, one level deep, nothing else in that folder.
    if (parts[0]!.toLowerCase() === DESIGNS_DIR) {
      if (parts.length === 2 && (lower.endsWith(EPD_EXT) || lower.endsWith(EPD_EXT + TMP)))
        return parts.join('/');
      throw new WriteGuardError(`refused: only ${EPD_EXT} documents are written in ${DESIGNS_DIR}/`);
    }
    if (parts[0]!.toLowerCase() !== EXPORTS_DIR)
      throw new WriteGuardError(`refused: "${relPath}" is outside ${EXPORTS_DIR}/`);
    return parts.join('/');
  }

  /** Deletions are only ever allowed for our own temp files (sidecar and .epd temps). */
  assertDeletable(relPath: string): string {
    const p = this.assertWritable(relPath);
    const lower = p.toLowerCase();
    if (
      !lower.endsWith(SIDECAR_SUFFIX + TMP) &&
      !(lower.startsWith(DESIGNS_DIR + '/') && lower.endsWith(EPD_EXT + TMP))
    )
      throw new WriteGuardError(`refused: deleting "${relPath}"`);
    return p;
  }
}

/** Never overwrite (§8): returns `name` or the first free `name_2`, `name_3`, … variant. */
export function uniqueName(name: string, exists: (candidate: string) => boolean): string {
  if (!exists(name)) return name;
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  for (let k = 2; k < 100000; k++) {
    const c = `${base}_${k}${ext}`;
    if (!exists(c)) return c;
  }
  throw new WriteGuardError('no free file name');
}
